/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// HistoryPanel.jsx — ported from the source app forecasting (verbatim layout)
import React from "react";
import "./HistoryPanel.css";

export default function HistoryPanel({ entries, onClose, onClear, onUndo }) {
  return (
    <div className="history-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="history-panel">

        {/* Header */}
        <div className="history-header">
          <div className="history-title">Change History</div>
          <div className="history-header-actions">
            {entries.length > 0 && (
              <button className="history-clear-btn" onClick={onClear}>Clear</button>
            )}
            <button className="history-close-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="history-sub">Changes made this session</div>

        {/* Entries */}
        <div className="history-list">
          {entries.length === 0 ? (
            <div className="history-empty">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              <div>No changes yet this session</div>
            </div>
          ) : (
            [...entries].reverse().map((e, i) => (
              <div key={i} className={`history-entry history-entry-${e.type}`}>
                <div className="history-entry-icon">
                  {e.type === "edit"   && <EditIcon />}
                  {e.type === "create" && <CreateIcon />}
                  {e.type === "paste"  && <PasteIcon />}
                  {e.type === "seed"   && <SeedIcon />}
                </div>
                <div className="history-entry-body">
                  <div className="history-entry-sku" title={e.sku}>{e.sku}</div>
                  <div className="history-entry-detail">
                    <span className="history-entry-location">{e.state} · {e.year}</span>
                    <span className="history-entry-month">{e.month}</span>
                  </div>
                  <div className="history-entry-values">
                    {e.type === "edit" && (
                      <>
                        <span className="history-old">{e.oldValue != null ? Number(e.oldValue).toLocaleString() : "—"}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                        <span className="history-new">{Number(e.newValue).toLocaleString()}</span>
                      </>
                    )}
                    {e.type === "create" && (
                      <span className="history-new">Created: {Number(e.newValue).toLocaleString()}</span>
                    )}
                    {e.type === "paste" && (
                      <span className="history-new">Pasted {e.monthCount} month{e.monthCount !== 1 ? "s" : ""}</span>
                    )}
                    {e.type === "seed" && (
                      <span className="history-new">Seeded {Number(e.cellCount).toLocaleString()} cell{e.cellCount !== 1 ? "s" : ""}</span>
                    )}
                  </div>
                  <div className="history-entry-meta">
                    <span className="history-user">{e.user}</span>
                    <span className="history-time">{formatTime(e.timestamp)}</span>
                  </div>
                </div>
                {e.undoData && (
                  <button
                    className="history-undo-btn"
                    onClick={() => onUndo(e)}
                    title="Undo this change"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                    </svg>
                    Undo
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(date) {
  const now  = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60)  return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}
function CreateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  );
}
function SeedIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h4l3 8 4-16 3 8h4" />
    </svg>
  );
}
function PasteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    </svg>
  );
}
